import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowClockwise, CreditCard, SignOut } from "@phosphor-icons/react";
import api, { apiError, logError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { eurHint } from "../lib/offers";
import { HelpWidget } from "../components/HelpWidget";

const OFFER_LABELS = { starter: "Starter", pro: "Pro", business: "Business" };
const PRICES = { starter: 199999, pro: 349999, business: 999999 };
const gnf = (n) => new Intl.NumberFormat("fr-FR").format(n) + " GNF";

export default function Payment() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const sessionId = params.get("session_id") || "";
  const offer = params.get("offre") || user?.company?.chosen_offer || "starter";
  const amount = PRICES[offer] || PRICES.starter;

  useEffect(() => { api.get("/billing/config").then((r) => setConfig(r.data)).catch((e) => logError(e)); }, []);

  const check = useCallback(async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/billing/payments/status/${sessionId}`);
      setStatus(data.payment_status);
      if (data.payment_status === "paid") {
        toast.success("Paiement confirmé — votre compte est activé");
        const fresh = await refresh();
        const step = fresh?.company?.onboarding_status || "active";
        navigate(step === "pending_setup" ? "/demarrage" : "/accueil", { replace: true });
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }, [sessionId, refresh, navigate]);

  useEffect(() => {
    if (!sessionId) return undefined;
    check();
    const t = setInterval(check, 4000);
    const stop = setTimeout(() => clearInterval(t), 60000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [sessionId, check]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        offer, provider: "stripe", origin_url: window.location.origin });
      window.location.assign(data.checkout_url);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 pb-28 sm:pb-8" data-testid="payment-page">
      <div className="w-full max-w-md fade-in space-y-4">
        <p className="font-display font-bold text-4xl lowercase tracking-tight text-center">kavizio</p>

        <div className="kcard p-6 space-y-4">
          <h1 className="font-display font-bold text-xl">Régler votre abonnement</h1>
          <div className="bg-[#F6F4EF] rounded-2xl p-4">
            <p className="text-sm text-gray-600">Offre {OFFER_LABELS[offer] || "Starter"}</p>
            <p data-testid="payment-amount" className="font-display font-bold text-3xl tabular-nums">
              {gnf(amount)}<span className="text-sm font-normal text-gray-500">/mois</span>
            </p>
            <p data-testid="payment-eur-hint" className="text-xs text-gray-500 mt-1">{eurHint(amount)}</p>
            <p className="text-xs text-gray-400 mt-1">
              Le prix de référence est en GNF. Le règlement par carte est débité en {config?.billing_currency || "EUR"} au taux du jour.
            </p>
          </div>

          {config && !config.configured ? (
            <div data-testid="payment-not-configured" className="text-sm bg-[#F7EFD9] text-[#8a6a14] rounded-xl px-3 py-2.5">
              Le paiement en ligne n'est pas encore activé. Contactez-nous pour régler votre abonnement : votre accès sera ouvert manuellement.
            </div>
          ) : (
            <>
              <button data-testid="payment-pay-btn" className="btn-amber w-full py-3 flex items-center justify-center gap-2"
                disabled={busy} onClick={pay}>
                <CreditCard size={17} /> {busy ? "Ouverture du paiement..." : "Payer par carte bancaire"}
              </button>
              <p className="text-xs text-gray-500 flex items-start gap-1.5">
                <ShieldCheck size={15} className="shrink-0 mt-0.5 text-[#2E7D5B]" />
                Paiement sécurisé par Stripe (Visa, Mastercard). Le paiement par Mobile Money sera ajouté prochainement.
              </p>
            </>
          )}

          {sessionId && (
            <button data-testid="payment-refresh-btn" onClick={check} disabled={busy}
              className="btn-ghost w-full py-2.5 text-sm flex items-center justify-center gap-1.5">
              <ArrowClockwise size={15} /> Vérifier mon paiement {status ? `(${status})` : ""}
            </button>
          )}
          <p className="text-xs text-gray-500">
            Votre compte s'active automatiquement dès que le paiement est confirmé par Stripe. Aucun accès n'est ouvert avant.
          </p>
          <div className="flex items-center justify-between gap-2 pt-1 border-t border-[#EFE9DE]">
            <button data-testid="payment-logout-btn" onClick={logout}
              className="text-xs text-gray-500 hover:text-[#16233D] flex items-center gap-1.5">
              <SignOut size={14} /> Se déconnecter
            </button>
            <span className="text-xs text-gray-400">Besoin d'aide ? Utilisez le bouton en bas à droite.</span>
          </div>
        </div>
      </div>
      <HelpWidget />
    </div>
  );
}
